import type { DepositUiSource } from "./depositUiSource"

/** GTM `dataLayer` custom event names (snake_case). */
export const AnalyticsEvent = {
  SessionStart: "session_start",
  ScreenView: "screen_view",
  BetPlaced: "bet_placed",
  TrickApplied: "trick_applied",
  DepositSheetOpen: "deposit_sheet_open",
  DepositInitiated: "deposit_initiated",
  DepositCompleted: "deposit_completed",
  DepositFailed: "deposit_failed",
  BetFailed: "bet_failed",
  WithdrawInitiated: "withdraw_initiated",
  GiftWithdrawInitiated: "gift_withdraw_initiated",
  WalletConnected: "wallet_connected",
  TournamentWelcomeView: "tournament_welcome_view",
  MarketplaceGiftPurchased: "marketplace_gift_purchased",
  ReferralShared: "referral_shared",
  PartnerCtaClick: "partner_cta_click",
  PromoCodeSubmitted: "promo_code_submitted",
  LegitCheckSheetView: "legit_check_sheet_view",
  LegitCheckExternalOpen: "legit_check_external_open",
  TelegramStartParam: "telegram_start_param",
  ReferralLinkCopied: "referral_link_copied",
  TournamentLeaderboardOpen: "tournament_leaderboard_open",
  SegmentSheetView: "segment_sheet_view",
  SegmentSheetCtaClick: "segment_sheet_cta_click",
} as const

export type TournamentLeaderboardOpenSource = "rewards_card" | "pvp_banner"

export type AnalyticsEventName =
  (typeof AnalyticsEvent)[keyof typeof AnalyticsEvent]

export type BetMode = "solo" | "squad"
export type BetAssetClass = "TON" | "TELEGRAM_GIFT"
export type DepositMethod = "ton_connect" | "cryptobot" | "xrocket"
export type WithdrawAssetClass = "TON" | "TELEGRAM_GIFT"

export type AnalyticsEventParams = {
  session_start: Record<string, never>
  screen_view: {
    page_path: string
    screen_name: string
  }
  bet_placed: {
    bet_mode: BetMode
    asset_class: BetAssetClass
    amount_ton?: number
    gift_count?: number
    round_id: string
    is_add_stake: boolean
  }
  trick_applied: {
    trick_type: string
    round_id: string
  }
  deposit_sheet_open: {
    deposit_ui_source: DepositUiSource
  }
  deposit_initiated: {
    deposit_method: DepositMethod
    amount_ton: number
    deposit_ui_source: DepositUiSource | "unknown"
  }
  deposit_completed: {
    deposit_method: DepositMethod
    /** Credited amount (balance delta), TON */
    amount_ton: number
    new_balance_ton: number
    deposit_ui_source: DepositUiSource | "unknown"
  }
  deposit_failed: {
    deposit_method: DepositMethod
    amount_ton: number
    failure_reason: "user_rejected" | "api_error" | "unknown"
    error_message?: string
    deposit_ui_source: DepositUiSource | "unknown"
  }
  bet_failed: {
    bet_mode: BetMode
    asset_class: BetAssetClass
    round_id: string
    amount_ton?: number
    gift_count?: number
    is_partial_failure: boolean
    error_message?: string
  }
  withdraw_initiated: {
    asset_class: WithdrawAssetClass
    amount_ton?: number
  }
  gift_withdraw_initiated: {
    gift_id: string
  }
  wallet_connected: {
    wallet_address: string
  }
  tournament_welcome_view: {
    tournament_id: string
    phase: "upcoming" | "active"
    open_source: "auto" | "manual"
  }
  marketplace_gift_purchased: {
    external_gift_id: string
    price_ton: number
  }
  referral_shared: Record<string, never>
  partner_cta_click: {
    destination: "telegram_bot"
  }
  promo_code_submitted: {
    result: "success" | "failed"
    code_length: number
  }
  legit_check_sheet_view: {
    round_id: string
    round_number: number
  }
  legit_check_external_open: {
    round_id: string
    round_number: number
  }
  telegram_start_param: {
    start_param: string
  }
  referral_link_copied: Record<string, never>
  tournament_leaderboard_open: {
    source: TournamentLeaderboardOpenSource
  }
  segment_sheet_view: {
    segment_id: string
    has_cta: boolean
    campaign_id?: string
  }
  segment_sheet_cta_click: {
    segment_id: string
    cta_key: string
    campaign_id?: string
  }
}
